import type { ProcedureType } from '@/types/cgt'
import type { EquipmentPurpose } from '@/types/equipment'
import { API_BASE_URL as BASE } from '@/lib/apiBase'
import { authFetch, authHeaders, handleUnauthorized } from '@/lib/authFetch'
import type { FieldDataType, FieldValidation } from '@/types/inventory'

// ─── Process step config (CGT-P2a backend) ──────────────────────────────────
//
// A "process step connection" ties a Process-type Field Library field to its
// inventory / equipment / staffing configuration. A field with no connection
// yet is normal — the read endpoints 404 in that case and we surface `null`.

export interface InventoryRequirement {
  id: string
  connectionId: string
  consumableId: string
  itemLabel: string | null
  /**
   * What this READ needs. Authored per unit, but a submission-scoped read
   * returns it already multiplied by the owning procedure's batch size — the
   * scaling is done server-side so this never has to hold a second copy of the
   * arithmetic that Start and End enforce.
   */
  qtyRequired: number
  /** The authored per-unit figure, present only on a submission-scoped read. */
  qtyRequiredPerUnit?: number
  uom: string | null
  order: number
}

export interface EquipmentRequirement {
  id: string
  connectionId: string
  equipmentTypeId: string
  /** Specific unit ("Equipment ID") picked for this requirement, when one was. */
  equipmentId: string | null
  /**
   * Prep before this unit is wanted. DELAYS the reservation rather than
   * lengthening it — see `src/lib/process/reservationWindow.ts`.
   */
  prepHrs: number
  setupHrs: number
  runHrs: number
  unloadHrs: number
  runsPerDay: number
  order: number
}

export interface StaffingLine {
  id: string
  connectionId: string
  staffingRoleId: string | null
  roleLabel: string | null
  prepHrs: number
  setupHrs: number
  runHrs: number
  unloadHrs: number
  numResources: number
  scheduleDays: string[] | null
  order: number
}

export interface ProcessStepConnection {
  id: string
  tenantId: string
  fieldId: string
  formDefinitionId: string
  sectionId: string | null
  dueDateConfig: Record<string, unknown> | null
  /**
   * The step's authored attributes, keyed by an attribute definition's `key` and
   * held as `{ active, value }` — the shape a material's attributes use.
   *
   * METHOD is deliberately not in here: a method is membership in
   * `MethodProcessStep`, so it arrives as `methods` below. A copy in this map
   * would drift from the Methods view, which is what the original design did.
   */
  attributes: Record<string, unknown>
  /**
   * The methods this step belongs to — one of its attributes, resolved by the
   * server so the operator's Attributes tab can name them. Absent on an older
   * backend, which reads as none.
   */
  methods?: { id: string; name: string }[]
  inventoryRequirements: InventoryRequirement[]
  equipmentRequirements: EquipmentRequirement[]
  staffingLines: StaffingLine[]
  /**
   * The owning form's category is `kit`, so running this step assembles a kit.
   * Derived server-side on every read — never stored on the connection, since a
   * form's category can change under it. Absent on the builder's upsert
   * responses, which return the raw row.
   */
  isKitStep?: boolean
  /**
   * The owning procedure's batch size on a submission-scoped read; 1 otherwise.
   * Above 1 it is what explains an otherwise unaccountable four-figure demand,
   * so surface it rather than showing a bare total.
   */
  quantityMultiplier?: number
  createdAt: string
  updatedAt: string
}

export interface ProcessStepSummary {
  inventoryComponents: number
  equipmentRequirements: number
  equipmentTimePerRun: number
  personHoursPerRun: number
}

/**
 * Mirrors the `PUT /process-steps/:fieldId` body.
 *
 * PARTIAL BY OMISSION. A child array included here REPLACES what is stored; one
 * left out is untouched, and `[]` clears it. Each tab therefore sends only its
 * own section — they used to have to echo each other's rows back on every save,
 * which is how saving an attribute came to delete a step's whole bill of
 * materials.
 */
export interface UpsertProcessStepPayload {
  /** Optional — a Field-Library process field is not bound to one form. */
  formDefinitionId?: string
  sectionId?: string | null
  inventoryRequirements?: Array<Partial<InventoryRequirement>>
  equipmentRequirements?: Array<Partial<EquipmentRequirement>>
  staffingLines?: Array<Partial<StaffingLine>>
  dueDateConfig?: Record<string, unknown> | null
  attributes?: Record<string, unknown>
}

// ─── Process step execution (CGT-P9a backend) ───────────────────────────────
//
// A patient form submission runs each of its Process fields through an
// Assigned → Started → Pending → Completed lifecycle. Every transition is
// e-signed and audit-trailed server-side.

export type ProcessStepExecutionStatus = 'ASSIGNED' | 'STARTED' | 'PENDING' | 'COMPLETED'

/**
 * One unit ("Equipment ID") the operator assigned to a configured
 * equipment-type requirement when starting the step. Labels are denormalized
 * server-side so the record reads even after registry changes.
 */
export interface EquipmentSelection {
  requirementId: string
  equipmentTypeId: string
  equipmentId: string
  equipmentLabel: string
  /** When this unit is expected to start (ISO); null when the operator left it blank. */
  expectedStart: string | null
}

/**
 * The lots the operator allocated to a configured material requirement when
 * starting the step (multi-lot split allowed; allocations sum to the
 * configured quantity — enforced server-side).
 */
export interface InventorySelection {
  requirementId: string
  consumableId: string
  itemLabel: string
  lots: Array<{ lot: string; qty: number }>
}

/**
 * A submission's process-step executions, plus who its steps INHERIT.
 *
 * The inherited assignee comes from the procedure's own step assignment and
 * exists because a step nobody has claimed has no execution row to carry one.
 * Without it, assigning a procedure left every untouched step reading
 * "unassigned" with Initiate disabled — the cascade had nothing to update.
 *
 * An execution's own `assigneeId` always wins: once a row exists it is the
 * record of who owns that step.
 */
export interface SubmissionExecutions {
  executions: ProcessStepExecution[]
  inheritedAssignee: { id: string; name: string } | null
  /**
   * The scheduled start of the procedure this submission belongs to, as an ISO
   * instant. Null for an ad-hoc form or a procedure with no schedule.
   *
   * The step's expected start falls back to this, so a run scheduled from the
   * procedures list proposes its own plan instead of an empty field.
   */
  scheduledStart: string | null
}

export interface ProcessStepExecution {
  id: string
  tenantId: string
  submissionId: string
  fieldId: string
  connectionId: string
  status: ProcessStepExecutionStatus
  actualStart: string | null
  actualEnd: string | null
  /** The planned start entered at Start — kept apart from the stamped actualStart. */
  plannedStart: string | null
  /** The user accountable for the run. Null = unassigned, which blocks Start. */
  assigneeId: string | null
  assignedAt: string | null
  /** Audit actor who made the assignment — a self-claim or a delegating supervisor. */
  assignedBy: string | null
  performedBy: string | null
  startedBy: string | null
  endedBy: string | null
  completedBy: string | null
  equipmentSelections: EquipmentSelection[] | null
  inventorySelections: InventorySelection[] | null
  /**
   * Operator-facing identity of the kit this run assembles (KIT-26-0121), or
   * null on any step that is not a kit step. Lives here rather than on the
   * connection because a connection is per field while a supply id is per run.
   */
  kitSupplyId?: string | null
  /**
   * Units the signed Start could not hold — a machine already booked, out of
   * service, and so on. **Only ever present on the response to `startExecution`**,
   * never on a listed execution, because it describes one attempt rather than
   * the row's state.
   *
   * A refusal does NOT mean the start failed: the step is running, it simply has
   * no hold on that unit. Refusing the whole start would spend the operator's
   * signature on a busy machine.
   */
  reservationRefusals?: StartBookingRefusal[]
  createdAt: string
  updatedAt: string
}

/**
 * One equipment hold a Start could not take.
 *
 * `reason` mirrors the backend's `ScanRefusalReason` plus `unavailable`, which
 * stands for "something failed that was not a recognisable refusal" — the
 * backend deliberately does not leak internal error text to the shop floor.
 */
export interface StartBookingRefusal {
  requirementId: string
  equipmentId: string
  message: string
  reason: ScanRefusalReason | 'unavailable'
}

/** One material requirement's FEFO proposal. Advisory — nothing is held. */
export interface InventoryProposal {
  requirementId: string
  consumableId: string
  itemLabel: string
  /** Already scaled by the procedure quantity, so it matches what Start demands. */
  qtyRequired: number
  /** `lot` is the lot NUMBER — the value Start matches allocations on. */
  lots: { lot: string; qty: number; expiryDate: string | null; locationCode: string | null }[]
  shortfall: number
  isShort: boolean
  alerts: { code: string; severity: 'info' | 'warning' | 'error'; message: string }[]
}

/**
 * One still-open reservation for a material requirement — a `RESERVED`
 * movement nothing has resolved yet. `qty` is the whole hold: a resolving
 * movement always closes its `RESERVED` row in full, so there is no fractional
 * remainder to return within a single lot.
 */
export interface ReturnableLot {
  lotId: string
  lotNumber: string
  qty: number
  expiryDate: string | null
  expired: boolean
}

/**
 * Where an unconsumed hold goes back to. `quarantine` additionally re-gates the
 * whole lot, so it also drops out of future FEFO picks.
 */
export type ReturnDestination = 'return_to_inventory' | 'quarantine'

/** One lot, and how much of its open hold goes back. */
export interface ReturnLine {
  lotId: string
  qty: number
  /**
   * Quarantine THIS lot instead of returning it to usable stock.
   *
   * Per lot because the gate is a status on the lot itself: a single
   * batch-level flag could not say WHICH lot it meant, so holding back one
   * suspect container shut away every other lot in the same return. Omitted,
   * `destination` decides.
   */
  quarantine?: boolean
}

export interface ReturnInventoryPayload {
  /** The default for every line that does not set `quarantine` itself. */
  destination: ReturnDestination
  /** Required by the server when anything in the return is quarantined. */
  reason?: string
  /**
   * What to return, per lot. Non-empty, each `qty` at most what that lot still
   * holds, and a lot may appear only once — the server refuses otherwise.
   */
  lines: ReturnLine[]
}

export interface ReturnInventoryResult {
  returned: number
  destination: ReturnDestination
}

/** One lot, and how much to reserve from it. */
export interface ReserveLine {
  lot: string
  qty: number
}

/**
 * Places unsigned holds for every currently ticked lot on one requirement —
 * before the transition itself is signed (z8nrz7dyfp follow-up), driven by
 * the "Reserve" button (mirrors equipment's own). Batched like
 * `ReturnInventoryPayload`, so one click commits every line atomically.
 * Undoing a pick calls `returnInventory` (`return_to_inventory`), the same as
 * the manual `[+]` return control; there is no separate "unreserve" endpoint.
 */
export interface ReserveInventoryPayload {
  lines: ReserveLine[]
}

export interface ReserveInventoryResult {
  reserved: number
}

/**
 * Bodies for the signed transitions. The signer is the authenticated user, and
 * `signingToken` is the proof they just re-entered their password — minted by
 * `authApi.createSigningToken`, single-use, and spent by the server before the
 * transition is applied.
 */
export interface StartExecutionPayload {
  signatureMeaning: string
  signingToken: string
  performedBy?: string
  actualStart?: string
  /** Planned start for the step as a whole; recorded next to actualStart. */
  plannedStart?: string
  comments?: string
  /** One selected unit per configured equipment-type requirement. */
  equipmentSelections?: Array<{
    requirementId: string
    equipmentId: string
    /** When that unit is expected to start (ISO). Planning metadata — never gates the start. */
    expectedStart?: string
  }>
  /** Lot allocations per configured material requirement. */
  inventorySelections?: Array<{
    requirementId: string
    lots: Array<{ lot: string; qty: number }>
  }>
}

/**
 * Partial selections save. Either field may be omitted; the server merges rather
 * than replaces, so patching only equipment never wipes recorded lots.
 */
export interface UpdateSelectionsPayload {
  equipmentSelections?: StartExecutionPayload['equipmentSelections']
  inventorySelections?: StartExecutionPayload['inventorySelections']
}

export interface EndExecutionPayload {
  signatureMeaning: string
  signingToken: string
  actualEnd?: string
  comments?: string
}

/**
 * Undo a start made in error (STARTED → ASSIGNED). `reason` is required — the
 * server rejects a blank one, since a voided record needs a stated cause.
 */
export interface UndoStartExecutionPayload {
  signatureMeaning: string
  signingToken: string
  reason: string
  comments?: string
}

export interface CompleteExecutionPayload {
  signatureMeaning: string
  signingToken: string
  comments?: string
}

/**
 * The Process page's "no form chosen" option. Purely a UI sentinel — it narrows
 * the pickers client-side and is never sent to the server.
 */
export const UNMAPPED_FORM = 'none'

/** A form that contains at least one Process field, with the fields it embeds. */
export interface FormWithProcesses {
  id: string
  /**
   * Lineage key — every version of one form shares it.
   *
   * This endpoint returns ONE ROW PER VERSION, so anything offering forms to a
   * user has to collapse them or the same form fills the list several times.
   * Collapse on this, never on `name`: names may repeat across unrelated
   * families (which is why lineage is keyed here and not on the name), so
   * name-grouping fuses two different forms into one entry and hides one form's
   * processes under the other's.
   */
  familyId: string
  name: string
  version: number | null
  /** Connection keys of the Process fields this form embeds. */
  fieldIds: string[]
}

/** One row of `GET /process-steps` — a field with a connection plus its child-row counts. */
export interface ProcessStepListItem {
  fieldId: string
  _count: {
    inventoryRequirements: number
    equipmentRequirements: number
    staffingLines: number
  }
  /**
   * Pairs in the connection's free-form `attributes` JSONB — the process
   * parameters the runtime shows as `key: value` chips under its Attributes tab.
   *
   * A count, not the JSON: the column is free-form and unbounded, and a list
   * endpoint drawing a table of numbers has no business shipping a blob per row.
   * The server owns what counts as a pair (`countProcessAttributes`), so this
   * number and the runtime tab's badge cannot drift apart.
   */
  attributeCount: number
}

// ─── Equipment reservations (P10 conflict-safe holds) ───────────────────────

/** A live hold on one equipment unit for one requirement's configured window. */
export interface EquipmentReservation {
  id: string
  tenantId: string
  equipmentId: string
  submissionId: string
  fieldId: string
  requirementId: string
  /** The PLANNED window — expected start + this requirement's prep, then its run. */
  startsAt: string
  endsAt: string
  /**
   * When the operator VERIFIED this unit by scanning it at the bench. Null
   * means nobody has been to the machine yet — which reads differently from
   * "it has not ended", so the UI says so in words rather than with a dash.
   */
  actualStart: string | null
  /** Stamped when the step ends, on verified units only. */
  actualEnd: string | null
  releasedAt: string | null
  reservedBy: string
}

/**
 * A reservation from `GET /process-steps/reservations/calendar`, carrying the
 * display context the row itself cannot hold: which unit, and which procedure booked
 * it. Every added field is null when the referent has since been deleted — the
 * hold is still real and still occupies the machine.
 */
export interface ReservationInWindow extends EquipmentReservation {
  equipmentLabel: string | null
  /**
   * The unit's status now; a booking made before it went out of service stands
   * and warns (z8nrz7fkgd). Absent from an older backend.
   */
  equipmentStatus?: 'OPERATIONAL' | 'OUT_OF_SERVICE' | null
  equipmentTypeId: string | null
  equipmentTypeName: string | null
  patientMrn: string | null
  formType: string | null
  /**
   * `reservedBy` resolved to a person's name, server-side.
   *
   * `reservedBy` itself is an actor string (`actorOf(user)` — an email in
   * practice) with no user id attached, and showing it raw is what put an
   * email address in the Assigned column. Null when no user matches the
   * recorded actor; optional so an older backend simply reads as unresolved.
   */
  reservedByName?: string | null
}

/**
 * The 409 body when a unit cannot be held.
 *
 * `busyUntil` IS NULLABLE, and callers must branch on it rather than formatting
 * it blindly. An out-of-service unit has no known free time — the server sends
 * `null` — and `new Date(null)` is the epoch, so an unguarded render shows
 * "Earliest free slot: 1/1/1970" to an operator holding a machine that is
 * simply unavailable. This type claimed `string` for a while and that is
 * precisely how such a render reached a regulated screen without tsc objecting.
 */
export interface ReservationConflict {
  message: string
  equipmentId: string
  // `daily_cap` carries a null `busyUntil` for the same reason
  // `out_of_service` does, and it is the second reason this field is nullable:
  // the unit is free at the asked-for time, so there is no instant to wait for.
  conflictType: 'reservation' | 'maintenance' | 'out_of_service' | 'daily_cap'
  busyUntil: string | null
}

export interface ReserveEquipmentItem {
  requirementId: string
  equipmentId: string
  /** The STEP's expected start. The server adds this requirement's prep to it. */
  startsAt: string
  /**
   * Set only when the operator overrode this row's time with its own picker.
   * The hold then opens EXACTLY here and prep is not applied — they have stated
   * the answer prep computes. Never pre-shift `startsAt` instead: the server
   * owns the prep arithmetic, so doing it here would apply it twice.
   */
  wantedAt?: string
}

// ─── Equipment scan verification (z8nrz7c8g4) ──────────────────────────────

/**
 * Why the server refused a scan. Mirrors `ScanRefusalReason` in
 * `cgt-backend/src/process-steps/process-step-reservations.service.ts` — kept in
 * sync by hand, like the rest of the domain dictionary (see CLAUDE.md).
 *
 * There is NO override member, deliberately. Unlike a mis-picked consumable, a
 * wrong machine is not recoverable, so the refusal is final.
 */
export const SCAN_REFUSAL_REASONS = [
  'unknown_code',
  'no_matching_requirement',
  // Keep this list in the SAME ORDER as the backend's so the two can be diffed
  // by eye. A member missing here does not fail to compile — it silently makes
  // `isScanRefusal` return false for a refusal the server really sent, and the
  // banner degrades to a generic message with `expected` and the requirement
  // label thrown away. `no_run_time` shipped backend-only and did exactly that.
  'no_run_time',
  'type_mismatch',
  'wrong_unit',
  'out_of_service',
  'migration_placeholder',
  'already_reserved',
  'conflict',
  // The unit is FREE at the requested time and has simply been turned round
  // its configured number of times today. Distinct from `conflict` because the
  // operator's next move is different: another unit or another day, not a
  // later slot on this one — and `busyUntil` is null for exactly that reason.
  'daily_cap',
] as const

export type ScanRefusalReason = (typeof SCAN_REFUSAL_REASONS)[number]

/** The refusal body, carrying what the step expected against what was scanned. */
export interface ScanRefusal {
  message: string
  reason: ScanRefusalReason
  expected: string | null
  scanned: string | null
  requirementId: string | null
  equipmentId: string | null
}

export interface ScanEquipmentInput {
  scannedCode: string
  requirementId?: string
  /** Omit to stamp the moment of the scan; send one to keep a planned window. */
  startsAt?: string
}

/** A verified scan: the unit was proved correct and is now held. */
export interface ScanEquipmentResult {
  reservation: EquipmentReservation
  requirementId: string
  equipmentId: string
  equipmentLabel: string
}

/**
 * One unit's availability for a window.
 *
 * ADVISORY. The server computes this without a lock, so a unit shown free can be
 * taken before you book it — the 409 path must stay handled regardless.
 */
export interface AvailabilitySlot {
  equipmentId: string
  equipmentTypeId: string
  available: boolean
  reason: 'reservation' | 'maintenance' | 'out_of_service' | 'daily_cap' | null
  /**
   * When it frees up. Null when free, when out of service, and when capped —
   * in the last case the unit is idle at the asked-for time and the next slot
   * is another day, which no instant inside this window can express.
   */
  busyUntil: string | null
}

/** One option in the process-step Assign To picker. */
export interface ProcessAssignableUser {
  id: string
  name: string
}

/** A process step assigned to the current user, with the context to act on it. */
export interface AssignedProcessStep extends ProcessStepExecution {
  /** Null when the submission has since been removed; the assignment row survives. */
  patientMrn: string | null
  formType: string | null
  submissionStatus: string | null
  // What My Work names the row by (2026-09-30). Null when the submission was
  // removed; absent from an older server.
  formName?: string | null
  formVersion?: number | null
  /** The process step's label on its form. */
  stepLabel?: string | null
  procedureId?: string | null
  procedureName?: string | null
  procedureType?: ProcedureType | null
  workOrderNumber?: string | null
}

/**
 * Thrown by `request`/`requestOrNull` for a non-ok response. `message` stays
 * byte-identical to the plain-Error format callers already parse; `body` is
 * the parsed JSON error payload (e.g. a `ReservationConflict`) when the
 * response was JSON, otherwise the raw response text.
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly body: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

function parseErrorBody(text: string): unknown {
  try {
    return JSON.parse(text) as unknown
  } catch {
    return text
  }
}

/** Narrows a caught error to an `ApiError` carrying a 409 `ReservationConflict` body. */
export function isReservationConflict(
  err: unknown,
): err is ApiError & { body: ReservationConflict } {
  return (
    err instanceof ApiError &&
    err.status === 409 &&
    typeof err.body === 'object' &&
    err.body !== null &&
    'conflictType' in err.body
  )
}

/**
 * Narrows a caught error to a refused equipment scan.
 *
 * Matches on `reason` rather than on status: the gate answers 400 for a wrong
 * machine and 409 for a busy or out-of-service one, and the banner renders both
 * the same way — expected versus scanned. Checked BEFORE
 * `isReservationConflict`, which would otherwise swallow the 409 cases and drop
 * the expected/scanned detail.
 *
 * The 409 bodies carry BOTH vocabularies deliberately (`conflictType` +
 * `busyUntil` as well as `reason` + `expected`/`scanned`), so a busy unit can be
 * rendered either as a scan refusal or as a conflict with an earliest-free time
 * without the server having to guess which surface asked. A body with no
 * `reason` is a plain conflict from the older reserve path.
 */
export function isScanRefusal(err: unknown): err is ApiError & { body: ScanRefusal } {
  return (
    err instanceof ApiError &&
    typeof err.body === 'object' &&
    err.body !== null &&
    'reason' in err.body &&
    SCAN_REFUSAL_REASONS.includes((err.body as ScanRefusal).reason)
  )
}

/**
 * All process-step requests are scoped to the selected tenant via the
 * `X-Tenant-Id` header. Mirrors the integrations client's local `request`.
 */
async function request<T>(path: string, tenantId: string, init?: RequestInit): Promise<T> {
  const res = await authFetch(`${BASE}${path}`, {
    headers: authHeaders({ 'X-Tenant-Id': tenantId, ...init?.headers }),
    ...init,
  })
  if (!res.ok) {
    handleUnauthorized(res.status)
    const text = await res.text().catch(() => res.statusText)
    throw new ApiError(
      `API ${init?.method ?? 'GET'} ${path} failed (${res.status}): ${text}`,
      res.status,
      parseErrorBody(text),
    )
  }
  return res.json() as Promise<T>
}

/**
 * Variant of `request` for the two read endpoints that legitimately 404 when a
 * field has no connection yet — resolves to `null` instead of throwing.
 */
async function requestOrNull<T>(path: string, tenantId: string): Promise<T | null> {
  const res = await authFetch(`${BASE}${path}`, {
    headers: authHeaders({ 'X-Tenant-Id': tenantId }),
  })
  if (res.status === 404) return null
  if (!res.ok) {
    handleUnauthorized(res.status)
    const text = await res.text().catch(() => res.statusText)
    throw new ApiError(
      `API GET ${path} failed (${res.status}): ${text}`,
      res.status,
      parseErrorBody(text),
    )
  }
  return res.json() as Promise<T>
}

export const processStepsApi = {
  list: (tenantId: string) => request<ProcessStepListItem[]>('/process-steps', tenantId),

  /** Forms worth offering in the Process page filter — those that have processes. */
  listFormsWithProcesses: (tenantId: string) =>
    request<FormWithProcesses[]>('/process-steps/forms', tenantId),

  /**
   * @param submissionId Read the step AS A RUN on this submission: inventory
   *   quantities come back multiplied by the owning procedure's batch size.
   *   Omit it (the form builder) to read the authored per-unit values.
   */
  get: (tenantId: string, fieldId: string, submissionId?: string) =>
    requestOrNull<ProcessStepConnection>(
      `/process-steps/${fieldId}${submissionId ? `?submissionId=${encodeURIComponent(submissionId)}` : ''}`,
      tenantId,
    ),

  /**
   * `get` for several Process fields at once, keyed by field id; null where a
   * field has no connection (z8nrz7e8kw). A run page loads every field of a
   * form with this instead of one request per field.
   */
  getMany: (tenantId: string, fieldIds: readonly string[], submissionId?: string) => {
    const qs = new URLSearchParams({ fieldIds: fieldIds.join(',') })
    if (submissionId) qs.set('submissionId', submissionId)
    return request<Record<string, ProcessStepConnection | null>>(`/process-steps/connections?${qs}`, tenantId)
  },

  getSummary: (tenantId: string, fieldId: string) =>
    requestOrNull<ProcessStepSummary>(`/process-steps/${fieldId}/summary`, tenantId),

  getOrCreate: (tenantId: string, fieldId: string, formDefinitionId: string) =>
    request<ProcessStepConnection>(`/process-steps/${fieldId}`, tenantId, {
      method: 'POST',
      body: JSON.stringify({ formDefinitionId }),
    }),

  update: (tenantId: string, fieldId: string, payload: UpsertProcessStepPayload) =>
    request<ProcessStepConnection>(`/process-steps/${fieldId}`, tenantId, {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),

  remove: (tenantId: string, fieldId: string) =>
    request<void>(`/process-steps/${fieldId}`, tenantId, { method: 'DELETE' }),

  listExecutions: (tenantId: string, submissionId: string) =>
    request<SubmissionExecutions>(`/process-steps/executions/${submissionId}`, tenantId),

  /**
   * What the system WOULD pick, first-expiry-first-out. Read-only: it holds
   * nothing, and the operator is free to change every line of it before Start.
   */
  inventoryProposal: (tenantId: string, submissionId: string, fieldId: string) =>
    request<InventoryProposal[]>(
      `/process-steps/executions/${submissionId}/${fieldId}/inventory-proposal`,
      tenantId,
    ),

  /**
   * What is still open to return for one material requirement. An empty array
   * means nothing is held — the `[+]` control renders nothing at all then.
   */
  getReturnableInventory: (
    tenantId: string,
    submissionId: string,
    fieldId: string,
    requirementId: string,
  ) =>
    request<ReturnableLot[]>(
      `/process-steps/executions/${submissionId}/${fieldId}/inventory/${requirementId}/returnable`,
      tenantId,
    ),

  /**
   * Place an unsigned hold the instant a lot is ticked, before Start is
   * signed. 400 when the lot doesn't exist, is quarantined, or can't cover
   * the requested qty.
   */
  reserveInventory: (
    tenantId: string,
    submissionId: string,
    fieldId: string,
    requirementId: string,
    payload: ReserveInventoryPayload,
  ) =>
    request<ReserveInventoryResult>(
      `/process-steps/executions/${submissionId}/${fieldId}/inventory/${requirementId}/reserve`,
      tenantId,
      { method: 'POST', body: JSON.stringify(payload) },
    ),

  /**
   * Release every open hold for this requirement early — before the step ends.
   * 400 when nothing is open (or quarantine arrives with no reason), 409 when
   * `return_to_inventory` is asked for on an expired lot.
   */
  returnInventory: (
    tenantId: string,
    submissionId: string,
    fieldId: string,
    requirementId: string,
    payload: ReturnInventoryPayload,
  ) =>
    request<ReturnInventoryResult>(
      `/process-steps/executions/${submissionId}/${fieldId}/inventory/${requirementId}/return`,
      tenantId,
      { method: 'POST', body: JSON.stringify(payload) },
    ),

  /**
   * Claim the step for yourself by omitting `assigneeId`. Passing another user's
   * id delegates and requires `process.assign`; passing your own is treated the
   * same as omitting it.
   */
  assignExecution: (
    tenantId: string,
    submissionId: string,
    fieldId: string,
    assigneeId?: string,
  ) =>
    request<ProcessStepExecution>(
      `/process-steps/executions/${submissionId}/${fieldId}/assign`,
      tenantId,
      { method: 'POST', body: JSON.stringify(assigneeId ? { assigneeId } : {}) },
    ),

  unassignExecution: (tenantId: string, submissionId: string, fieldId: string) =>
    request<ProcessStepExecution>(
      `/process-steps/executions/${submissionId}/${fieldId}/assign`,
      tenantId,
      { method: 'DELETE' },
    ),

  /**
   * Partial save of what the run actually used, while it is ASSIGNED or STARTED.
   * Not e-signed — completeness is enforced at End, not here. Only the assignee
   * may call it.
   */
  updateSelections: (
    tenantId: string,
    submissionId: string,
    fieldId: string,
    payload: UpdateSelectionsPayload,
  ) =>
    request<ProcessStepExecution>(
      `/process-steps/executions/${submissionId}/${fieldId}/selections`,
      tenantId,
      { method: 'PATCH', body: JSON.stringify(payload) },
    ),

  /**
   * Record the step's expected start before Start is signed.
   *
   * Its own route rather than a field on `updateSelections`, for two reasons:
   * that route is refused unless the caller IS the assignee, and planning a run
   * for someone else is ordinary work; and it 404s when the step has no
   * execution row yet, which is the normal state of a step whose assignee is
   * inherited from its procedure. This one creates the row.
   *
   * `null` clears the plan. Refused once the step has started — from then on
   * the value is part of the signed record.
   */
  setPlannedStart: (
    tenantId: string,
    submissionId: string,
    fieldId: string,
    plannedStart: string | null,
  ) =>
    request<ProcessStepExecution>(
      `/process-steps/executions/${submissionId}/${fieldId}/planned-start`,
      tenantId,
      { method: 'PATCH', body: JSON.stringify({ plannedStart }) },
    ),

  startExecution: (
    tenantId: string,
    submissionId: string,
    fieldId: string,
    payload: StartExecutionPayload,
  ) =>
    request<ProcessStepExecution>(
      `/process-steps/executions/${submissionId}/${fieldId}/start`,
      tenantId,
      { method: 'POST', body: JSON.stringify(payload) },
    ),

  undoStartExecution: (
    tenantId: string,
    submissionId: string,
    fieldId: string,
    payload: UndoStartExecutionPayload,
  ) =>
    request<ProcessStepExecution>(
      `/process-steps/executions/${submissionId}/${fieldId}/undo-start`,
      tenantId,
      { method: 'POST', body: JSON.stringify(payload) },
    ),

  endExecution: (
    tenantId: string,
    submissionId: string,
    fieldId: string,
    payload: EndExecutionPayload,
  ) =>
    request<ProcessStepExecution>(
      `/process-steps/executions/${submissionId}/${fieldId}/end`,
      tenantId,
      { method: 'POST', body: JSON.stringify(payload) },
    ),

  completeExecution: (
    tenantId: string,
    submissionId: string,
    fieldId: string,
    payload: CompleteExecutionPayload,
  ) =>
    request<ProcessStepExecution>(
      `/process-steps/executions/${submissionId}/${fieldId}/complete`,
      tenantId,
      { method: 'POST', body: JSON.stringify(payload) },
    ),

  /**
   * Availability for several windows at once, keyed by the caller (requirement
   * id, in practice). Each requirement has its own start and duration, so one
   * spanning window would report a unit busy for requirement B because it is
   * booked during A's slot.
   */
  availabilityForWindows: (
    tenantId: string,
    windows: {
      key: string
      from: string
      to: string
      /**
       * The requirement's throughput cap. Send it and a unit already turned
       * round that many times shows as unavailable, matching what booking
       * would do; omit it and only overlap is checked.
       */
      runsPerDay?: number
      /**
       * This requirement's own live hold. Booking releases it before counting,
       * so without this an already-booked step previews itself as the thing
       * using up the day.
       */
      excludeReservationIds?: string[]
    }[],
  ) =>
    request<Record<string, AvailabilitySlot[]>>(
      '/process-steps/reservations/availability',
      tenantId,
      { method: 'POST', body: JSON.stringify({ windows }) },
    ),

  /** Advisory availability for a window — never a substitute for handling a 409. */
  equipmentAvailability: (tenantId: string, from: string, to: string) =>
    request<AvailabilitySlot[]>(
      `/process-steps/reservations/availability?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
      tenantId,
    ),

  /** Every live hold in a window, tenant-wide — for the equipment calendar and Tasks. */
  reservationsInWindow: (tenantId: string, from: string, to: string, purposes?: readonly EquipmentPurpose[]) =>
    request<ReservationInWindow[]>(
      `/process-steps/reservations/calendar?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}${
        purposes?.length ? `&purposes=${encodeURIComponent(purposes.join(','))}` : ''
      }`,
      tenantId,
    ),

  /**
   * The caller's own assigned steps, or `assigneeId`'s: the server allows
   * another person only to a caller who may see the team (403 otherwise).
   */
  listAssignedToMe: (tenantId: string, assigneeId?: string) =>
    request<AssignedProcessStep[]>(
      `/process-steps/executions/assigned${assigneeId ? `?${new URLSearchParams({ assigneeId })}` : ''}`,
      tenantId,
    ),

  /**
   * Roster for the process-step Assign To picker. Gated `process.execute`, so
   * anyone who can run a step can populate it.
   *
   * With `fieldId`, the server narrows it to the people qualified to work that
   * step — whoever holds a method covering it. A step no method covers returns
   * the full roster, so this stays correct before the matrix is populated.
   */
  listAssignableUsers: (tenantId: string, fieldId?: string) =>
    request<ProcessAssignableUser[]>(
      fieldId
        ? `/process-steps/executions/assignable-users?fieldId=${encodeURIComponent(fieldId)}`
        : '/process-steps/executions/assignable-users',
      tenantId,
    ),

  /** `listAssignableUsers` for several steps at once, keyed by field id (z8nrz7e8kw). */
  listAssignableUsersByField: (tenantId: string, fieldIds: readonly string[]) =>
    request<Record<string, ProcessAssignableUser[]>>(
      `/process-steps/executions/assignable-users/by-field?fieldIds=${encodeURIComponent(fieldIds.join(','))}`,
      tenantId,
    ),

  /** `listReservations` for several steps of one submission, keyed by field id (z8nrz7e8kw). */
  listReservationsByField: (tenantId: string, submissionId: string, fieldIds: readonly string[]) =>
    request<Record<string, EquipmentReservation[]>>(
      `/process-steps/reservations/live?${new URLSearchParams({ submissionId, fieldIds: fieldIds.join(',') })}`,
      tenantId,
    ),

  listReservations: (tenantId: string, submissionId: string, fieldId: string) =>
    request<EquipmentReservation[]>(
      `/process-steps/reservations/${submissionId}/${fieldId}`,
      tenantId,
    ),

  reserveEquipment: (
    tenantId: string,
    submissionId: string,
    fieldId: string,
    items: ReserveEquipmentItem[],
  ) =>
    request<EquipmentReservation[]>(
      `/process-steps/reservations/${submissionId}/${fieldId}`,
      tenantId,
      { method: 'POST', body: JSON.stringify({ items }) },
    ),

  /**
   * Move every live hold on a step to a new expected start.
   *
   * ALL OR NOTHING, and server-side for that reason: doing this here as release
   * then re-reserve would have dropped the other holds by the time one unit
   * turned out to be busy. Only the new start is sent — the windows are rebuilt
   * from the step's own configuration, so this client cannot propose times that
   * disagree with the prep the builder configured.
   */
  retimeReservations: (
    tenantId: string,
    submissionId: string,
    fieldId: string,
    startsAt: string,
    /**
     * Move only this row's hold, treating `startsAt` as its exact window start.
     * Omit to re-plan the whole step, where each hold gets its own prep added.
     */
    requirementId?: string,
  ) =>
    request<EquipmentReservation[]>(
      `/process-steps/reservations/${submissionId}/${fieldId}/retime`,
      tenantId,
      { method: 'POST', body: JSON.stringify({ startsAt, requirementId }) },
    ),

  /**
   * Verify a scanned equipment tag and hold the unit in one call.
   *
   * The verification is the SERVER's, not this client's. The screen presents
   * the outcome; it never decides it — a client-side check is defeated by any
   * direct API call, and this is a regulated gate.
   */
  scanEquipment: (
    tenantId: string,
    submissionId: string,
    fieldId: string,
    input: ScanEquipmentInput,
  ) =>
    request<ScanEquipmentResult>(
      `/process-steps/reservations/${submissionId}/${fieldId}/scan`,
      tenantId,
      { method: 'POST', body: JSON.stringify(input) },
    ),

  /**
   * Mark a held unit as started by hand, stamping its actual start.
   *
   * The manual counterpart to scanning. The server audits it as
   * `started_manually` rather than `scan_verified` — same timestamp, different
   * provenance, and the record keeps them apart.
   */
  startReservation: (tenantId: string, reservationId: string) =>
    request<EquipmentReservation>(
      `/process-steps/reservations/${reservationId}/start`,
      tenantId,
      { method: 'POST' },
    ),

  releaseReservation: (tenantId: string, reservationId: string) =>
    request<EquipmentReservation>(`/process-steps/reservations/${reservationId}`, tenantId, {
      method: 'DELETE',
    }),

  /** The attribute definitions a step's Field Attributes panel is built from. */
  listFieldDefs: (tenantId: string) =>
    request<ProcessFieldDef[]>('/process-steps/field-defs', tenantId),

  // Each write answers with the WHOLE list, so the editor re-renders from one
  // response instead of merging a row into what it already had.

  createFieldDef: (tenantId: string, payload: ProcessFieldDefPayload) =>
    request<ProcessFieldDef[]>('/process-steps/field-defs', tenantId, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  /** `key` is immutable — stored values are keyed on it. */
  updateFieldDef: (
    tenantId: string,
    id: string,
    payload: Partial<Omit<ProcessFieldDefPayload, 'key'>>,
  ) =>
    request<ProcessFieldDef[]>(`/process-steps/field-defs/${id}`, tenantId, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),

  /** `orphaned` is how many steps held a value for it. */
  removeFieldDef: (tenantId: string, id: string) =>
    request<{ ok: true; orphaned: number }>(`/process-steps/field-defs/${id}`, tenantId, {
      method: 'DELETE',
    }),
}

export interface ProcessFieldDefPayload {
  key: string
  label: string
  description?: string
  dataType?: FieldDataType
  order?: number
  activeByDefault?: boolean
  options?: string[]
  validation?: FieldValidation | null
}

/**
 * One attribute DEFINITION for a process step, from
 * `GET /process-steps/field-defs`.
 *
 * The same shape an inventory field def has, minus `profileType`. `options` is
 * resolved server-side, which is how the `method` def offers the tenant's
 * methods without storing a copy of their names.
 */
export interface ProcessFieldDef {
  id: string
  key: string
  label: string
  description: string | null
  dataType: FieldDataType
  order: number
  activeByDefault: boolean
  options: string[]
  validation: FieldValidation | null
  /**
   * The value is held as a RELATION, not in the attributes map — true for
   * `method` and nothing else today. The editor saves it through the step's
   * methods endpoint instead.
   */
  relational: boolean
}
